import * as React from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";
import { addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { formatDayHeader, isSameDay } from "./utils";
import type { DailyTimelineLabels } from "./types";

interface DayHeaderProps {
  date: Date;
  now: Date;
  labels: DailyTimelineLabels;
  onDateChange?: (next: Date) => void;
  isLargeMode?: boolean;
}

export function DayHeader({ date, now, labels, onDateChange, isLargeMode }: DayHeaderProps) {
  const isToday = isSameDay(date, now);
  const headline = formatDayHeader(date, labels.weekdayShort, labels.monthsLong);
  const [showJumpToday, setShowJumpToday] = React.useState(!isToday);

  React.useEffect(() => {
    setShowJumpToday(!isToday);
    if (!isToday) {
      const t = window.setTimeout(() => setShowJumpToday(false), 4000);
      return () => window.clearTimeout(t);
    }
  }, [isToday, date]);

  const touchStart = React.useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    const threshold = window.innerWidth * 0.3;
    if (Math.abs(dx) >= threshold) {
      onDateChange?.(addDays(date, dx < 0 ? 1 : -1));
    }
    touchStart.current = null;
  };

  const dateBtn = "inline-flex items-center justify-center h-14 w-14 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground transition-colors biz-focus-ring biz-motion-safe";

  return (
    <header
      className="sticky top-0 z-20 bg-biz-bg/95 backdrop-blur border-b border-biz-border"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-3 py-2 gap-2">
        <button
          type="button"
          onClick={() => onDateChange?.(addDays(date, -1))}
          aria-label={labels.prevDay}
          className={dateBtn}
        >
          <CaretLeft size={20} weight="regular" />
        </button>
        <div className="flex-1 min-w-0 text-center">
          <h1 className={cn(
            "font-display font-semibold text-biz-foreground tracking-tight truncate",
            isLargeMode ? "text-xl" : "text-lg",
          )}>
            {headline}
          </h1>
          {isToday && (
            <div className="font-mono-num text-[10px] uppercase tracking-wider text-biz-primary mt-0.5">
              {labels.today}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDateChange?.(addDays(date, 1))}
          aria-label={labels.nextDay}
          className={dateBtn}
        >
          <CaretRight size={20} weight="regular" />
        </button>
      </div>
      {showJumpToday && !isToday && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => onDateChange?.(now)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-biz-btn text-xs font-semibold bg-biz-muted text-biz-foreground hover:bg-biz-border transition-colors biz-focus-ring biz-motion-safe"
          >
            <CalendarBlank size={14} weight="regular" />
            {labels.today}
          </button>
        </div>
      )}
    </header>
  );
}
